import {classes} from '@bitstillery/common/lib/utils'
import {unique_id} from '@bitstillery/common/lib/utils'
import {Button} from '@bitstillery/common/components/ui/button/button'
import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import {MithrilComponent} from '@bitstillery/mithril'
import {state, watch} from '@bitstillery/mithril'
import {logger} from '@bitstillery/mithril'
import {debounce, pending, present} from '@bitstillery/common/lib/utils'
import m from '@bitstillery/mithril'

import type {FieldSearchAttrs, FieldSearchOption} from '@bitstillery/common/types/field'
import {Signal} from '@bitstillery/mithril'
import type {Unwatch} from '@bitstillery/mithril'
import {html_element} from '@bitstillery/common/lib/dom'
import {failure_message} from '@bitstillery/common/lib/validation'
// Navigation keys are keys that change the selection of the suggestion dropdown.
const NAVIGATION_KEYS = new Set(['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp'])

interface FieldSearchData {
    focussed: boolean
    input: string
    password: {
        hide: boolean
    }
    search: {
        selected: number | null
    }
}

export class FieldSearch extends MithrilComponent<FieldSearchAttrs> {
    $suggestion_container: HTMLElement | null = null

    data: FieldSearchData = state(
        {
            focussed: false,
            input: '',
            password: {
                hide: true,
            },
            search: {
                selected: null,
            },
        },
        'field-search-data',
    )

    query: (input: string) => unknown = pending()

    name = unique_id()
    watchers: Unwatch[] = []

    override oncreate(vnode: m.VnodeDOM<FieldSearchAttrs> & {dom: HTMLElement}) {
        this.query = debounce(300, () => {
            void present(vnode.attrs.search.query, 'search query')(this.data.input)
        })
        const linked = present(vnode.attrs.search.linked, 'search linked model')
        // The linked model is a state proxy, which answers `$key` with that key's signal.
        const linked_signal: unknown = Reflect.get(linked[0], `$${linked[1]}`)
        if (linked_signal instanceof Signal) {
            this.watchers.push(
                watch(linked_signal, (linked_value: unknown) => {
                    if (!linked_value) {
                        this.data.input = ''
                        vnode.attrs.search.options.length = 0
                        this.data.focussed = false
                    } else if (typeof linked_value === 'string' || typeof linked_value === 'number') {
                        this.data.input = String(linked_value)
                    }
                }),
            )
        }

        if (vnode.attrs.autofocus) {
            setTimeout(() => vnode.dom.focus(), 100)
        }
        this.$suggestion_container = vnode.dom.querySelector<HTMLElement>('.search-suggestions')
    }

    override onremove() {
        this.watchers.map((unwatch) => unwatch())
    }

    navigate_keyboard(vnode: m.Vnode<FieldSearchAttrs>, e: KeyboardEvent) {
        if (e.key === 'Backspace') {
            this.data.search.selected = null
            return
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            if (vnode.attrs.search.options.length > 0) {
                // The highlighted index outlives a refined search that returned fewer options.
                const suggestion =
                    this.data.search.selected !== null ? vnode.attrs.search.options[this.data.search.selected] : undefined
                if (suggestion) {
                    this.data.input = vnode.attrs.search.linked_display === 'value' ? String(suggestion.value) : suggestion.label
                    this.onsuggestion(vnode, suggestion)
                }
            }
            return
        }

        if (vnode.attrs.search.options.length === 0) {
            this.data.search.selected = null
            return
        }

        if (!NAVIGATION_KEYS.has(e.key) || ['ArrowLeft', 'ArrowRight'].includes(e.key)) {
            return
        }

        if (this.data.search.selected === null) {
            this.data.search.selected = 0
        } else {
            if (e.key === 'ArrowDown') {
                if (this.data.search.selected < vnode.attrs.search.options.length - 1) {
                    this.data.search.selected += 1
                }
            } else if (e.key === 'ArrowUp') {
                if (this.data.search.selected > 0) {
                    this.data.search.selected -= 1
                } else {
                    this.data.search.selected = 0
                }
            } else if (e.key === 'PageDown') {
                if (this.data.search.selected < vnode.attrs.search.options.length - 5) {
                    this.data.search.selected += 5
                } else {
                    this.data.search.selected = vnode.attrs.search.options.length - 1
                }
            } else if (e.key === 'PageUp') {
                if (this.data.search.selected > 4) {
                    this.data.search.selected -= 5
                } else {
                    this.data.search.selected = 0
                }
            }
        }

        // Scroll the selected option into view for arrow key navigation
        this.$suggestion_container = html_element(vnode.dom)?.querySelector<HTMLElement>('.search-suggestions') ?? null
        if (this.$suggestion_container?.children.length) {
            const selected_option = this.$suggestion_container.children[this.data.search.selected]
            if (selected_option) {
                selected_option.scrollIntoView({behavior: 'smooth', block: 'nearest'})
            }
        }
    }

    onsuggestion(vnode: m.Vnode<FieldSearchAttrs>, suggestion: FieldSearchOption) {
        logger.debug(`[FieldSearch] exact suggestion match: ${suggestion.value}`)
        if (vnode.attrs.search.onsuggestion) {
            vnode.attrs.search.onsuggestion(suggestion)
        }
    }

    view(vnode: m.Vnode<FieldSearchAttrs>) {
        const {validation} = vnode.attrs

        if (vnode.attrs.validation && this.data.input) {
            vnode.attrs.validation.dirty = true
        }

        // Add human readable description to the validation object.
        if (validation && vnode.attrs.label) {
            validation.description = vnode.attrs.label
        }

        const invalid = validation ? validation._invalid : false
        const {disabled} = vnode.attrs

        return (
            <div
                id={`field-search-${this.name}`}
                className={classes('c-field-search', 'field', vnode.attrs.className, {
                    disabled: disabled,
                    invalid: validation && invalid && validation.dirty,
                    valid: validation && !invalid && validation.dirty,
                })}
            >
                {vnode.attrs.label && (
                    <label>
                        {vnode.attrs.label}
                        {vnode.attrs.icon && <Icon name={vnode.attrs.icon} />}
                        {validation && <span className='validation'>{validation.label}</span>}
                    </label>
                )}

                {(() => {
                    if (vnode.attrs.search.linked && Reflect.get(vnode.attrs.search.linked[0], vnode.attrs.search.linked[1])) {
                        const option = vnode.attrs.search.options.find((i) => i.value === this.data.input)
                        const linked_text =
                            option && vnode.attrs.search.linked_display === 'value'
                                ? String(option.value)
                                : option
                                  ? option.label
                                  : this.data.input

                        return [
                            <div className='search-linked'>
                                <div className='linked-label'>{linked_text}</div>
                                <Button
                                    className='c-button'
                                    icon='close'
                                    onclick={() => {
                                        vnode.attrs.search.options.length = 0
                                        this.data.input = ''
                                        if (vnode.attrs.search.onreset) {
                                            vnode.attrs.search.onreset()
                                        }
                                    }}
                                    tip='Clear selection'
                                    type='info'
                                />
                            </div>,
                            (() => {
                                if (invalid && validation?.dirty) {
                                    return <div className='help validation'>{failure_message(invalid)}</div>
                                } else if (vnode.attrs.help) {
                                    return <div class='help'>{vnode.attrs.help}</div>
                                }
                                return undefined
                            })(),
                        ]
                    }

                    return (
                        <div className='input-wrapper'>
                            <input
                                autofocus={vnode.attrs.autofocus}
                                disabled={vnode.attrs.disabled}
                                id={vnode.attrs.id}
                                onkeydown={(e: KeyboardEvent) => {
                                    this.navigate_keyboard(vnode, e)
                                }}
                                onblur={() => {
                                    // Allow the onclick event handler to be called, before its cleaned up.
                                    setTimeout(() => (this.data.focussed = false), 100)
                                }}
                                onfocus={() => {
                                    this.query('')
                                    this.data.focussed = true
                                }}
                                oninput={(e) => {
                                    const {value} = e.currentTarget
                                    if (vnode.attrs.validation) {
                                        vnode.attrs.validation.dirty = true
                                    }

                                    this.data.input = value
                                    this.query(value)
                                }}
                                placeholder={vnode.attrs.placeholder ? vnode.attrs.placeholder : ''}
                                type='text'
                                value={this.data.input}
                            />

                            <div
                                className={classes('search-suggestions', {
                                    hidden: !this.data.focussed,
                                })}
                                onclick={(e) => {
                                    const clicked_value = html_element(e.target)?.dataset.id
                                    const suggestion_index = vnode.attrs.search.options.findIndex((i) => {
                                        if (typeof i.value === 'string') {
                                            return i.value === clicked_value
                                        }
                                        return i.value === Number(clicked_value)
                                    })
                                    const suggestion = vnode.attrs.search.options[suggestion_index]
                                    // A click on the list itself rather than on one of its options.
                                    if (!suggestion) {
                                        return
                                    }
                                    this.data.input = String(suggestion.value)
                                    this.onsuggestion(vnode, suggestion)
                                }}
                                tabindex='-1'
                            >
                                {vnode.attrs.search.options.map((suggestion, index: number) => (
                                    <div
                                        className={classes('suggestion', {
                                            selected: index === this.data.search.selected,
                                        })}
                                        data-id={suggestion.value}
                                    >
                                        {suggestion.label}
                                    </div>
                                ))}
                            </div>

                            {(() => {
                                if (invalid && validation?.dirty) {
                                    return <div className='help validation'>{failure_message(invalid)}</div>
                                } else if (vnode.attrs.help) {
                                    return <div class='help'>{vnode.attrs.help}</div>
                                }
                                return undefined
                            })()}
                        </div>
                    )
                })()}
            </div>
        )
    }
}
